import { EventEmitter } from '@spotify-internal/emitter';
import { Logger } from '@spotify-internal/transport/lib/logger';
import { logging } from '@js-sdk/debug-tools';
import { ProductStateObserver, } from '@spotify-internal/transport/lib/product_state_observer';
import { TransportEvent, } from '@spotify-internal/transport';
import { GAIA_VOLUME, } from '../typedefs';
import { HarmonyErrors } from '../enums/errors';
import { HarmonyErrorSource } from '../enums/error_source';
import { ManifestFormat } from '../enums/manifest_format';
import { HarmonyError } from '../error/harmony_error';
import { defer } from './helpers/defer';
import { DeviceDescriptor, } from './device_descriptor';
import { interpolateUA } from './helpers/interpolate_ua';
import sdkVersion from '../version';
const debugLogger = logging.forTag('harmonyclient');
const VALID_DEVICE_ID_EXP = /^[a-zA-Z0-9_%:-]{1,40}$/;
export class HarmonyClient extends EventEmitter {
    /**
     * Represents a client or device.
     *
     * @param options - The options for this current client.
     */
    constructor(options) {
        super();
        /**
         * A promise that will eventually be resolved to the system information string
         * that will be passed to the AP connection.
         */
        this._systemInfo = defer();
        /**
         * A promise that will eventually be resolved to the user specified version
         * of the client. Used for logging.
         */
        this._clientVersion = defer();
        if (!options) {
            throw new TypeError('Argument `options` is not defined.');
        }
        if (!options.transport) {
            throw new TypeError('No Transport instance provided');
        }
        if (!options.transport.hasPlugin('dealer')) {
            throw new TypeError('Transport has no Dealer plugin.');
        }
        this._transport = options.transport;
        this._deviceDescriptor = this._initDeviceDescriptor(options);
        this._logger = new Logger({
            transport: this._transport,
            sdkId: this.getSDKId(),
            platform: this._systemInfo.promise,
            clientVersion: this._clientVersion.promise,
        });
        this._productStateObserver = ProductStateObserver.create({
            transport: this._transport,
        });
        this._onConnected = this._onConnected.bind(this);
        this._onConnectionError = this._onConnectionError.bind(this);
        this._onAuthenticationError = this._onAuthenticationError.bind(this);
        this._onShortSessionDisconnect = this._onShortSessionDisconnect.bind(this);
        this._attachListeners();
    }
    static create(options) {
        return new HarmonyClient(options);
    }
    // FIXME: Remove
    static of(item) {
        return item._client;
    }
    _createDeviceDescriptor(options, deviceInfo = {}) {
        var _a, _b;
        if ((deviceInfo === null || deviceInfo === void 0 ? void 0 : deviceInfo.id) && !VALID_DEVICE_ID_EXP.test(deviceInfo.id)) {
            throw HarmonyError.fatal(HarmonyErrors.HARMONY_INVALID_DESCRIPTOR_ID, 'Invalid device id.');
        }
        // Certain capabilities are required by the system in order to work properly.
        const capabilities = deviceInfo.capabilities || {};
        const deviceCapabilities = {
            change_volume: (_a = capabilities.change_volume) !== null && _a !== void 0 ? _a : true,
            endsong_snooping: capabilities.endsong_snooping,
            enable_play_token: true,
            supports_file_media_type: true,
            play_token_lost_behavior: options.playTokenLostBehavior || 'pause',
            disable_connect: !!options.hidden,
            // Audio Features
            audio_podcasts: (_b = capabilities.audio_podcasts) !== null && _b !== void 0 ? _b : true,
            // Video Features
            video_playback: capabilities.video_playback,
            // Volume steps need to be within 0 to 65534 (because max volume is 65535)
            volume_steps: capabilities.volume_steps !== undefined &&
                capabilities.volume_steps >= 0 &&
                capabilities.volume_steps < GAIA_VOLUME
                ? capabilities.volume_steps
                : undefined,
            // We only define the ads manifest formats here. All other manifest formats
            // are defined by Streamer (see streamer.js)
            manifest_formats: [ManifestFormat.FILE_URLS_MP3],
        };
        const descriptor = DeviceDescriptor.create(Object.assign(Object.assign({}, deviceInfo), { capabilities: deviceCapabilities }));
        this._systemInfo.resolve(descriptor.getPlatformIdentifier());
        this._clientVersion.resolve(descriptor.getPlatformVersion());
        this.proxyEmit(descriptor, "descriptor_changed" /* DEVICE_DESCRIPTOR_CHANGED */, "descriptor_changed" /* CLIENT_DEVICE_DESCRIPTOR_CHANGED */);
        return descriptor;
    }
    _initDeviceDescriptor(options) {
        return Promise.resolve(options.descriptor)
            .then(this._createDeviceDescriptor.bind(this, options))
            .catch((e) => {
            this._onError(HarmonyErrorSource.HARMONY, { error: e });
            return Promise.reject(e);
        });
    }
    _attachListeners() {
        // This cast is needed in order to make emitter type checking work for
        // proxying.
        const transport = this._transport;
        transport.on(TransportEvent.CONNECTED, this._onConnected);
        transport.on(TransportEvent.CONNECTION_FAILED, this._onConnectionError);
        transport.on(TransportEvent.AUTHENTICATION_FAILED, this._onAuthenticationError);
        this.proxyEmit(transport, TransportEvent.AUTHENTICATED, "authenticated" /* CLIENT_AUTHENTICATED */);
        this.proxyEmit(transport, TransportEvent.RECONNECTING, "reconnecting" /* CLIENT_RECONNECTING */);
        this.proxyEmit(transport, TransportEvent.RECONNECTED, "reconnected" /* CLIENT_RECONNECTED */);
        this.proxyEmit(transport, TransportEvent.LOGGED_OUT, "logged_out" /* CLIENT_LOGGED_OUT */);
        this.proxyEmitSync(transport, TransportEvent.BEFORE_OFFLINE_DISCONNECT, "before_offline_disconnect" /* CLIENT_BEFORE_OFFLINE_DISCONNECT */);
        this.proxyEmitSync(transport, TransportEvent.BEFORE_ONLINE_DISCONNECT, "before_disconnect" /* CLIENT_BEFORE_DISCONNECT */);
        transport.on(TransportEvent.SHORT_SESSION_DISCONNECTED, this._onShortSessionDisconnect.bind(this));
        // This cast is needed in order to make emitter type checking work for
        // proxying.
        this.proxyEmit(this._productStateObserver, TransportEvent.PRODUCT_STATE_CHANGED, "product_state_changed" /* CLIENT_PRODUCT_STATE_CHANGED */);
        this.on("before_disconnect" /* CLIENT_BEFORE_DISCONNECT */, (e) => {
            const promise = this._productStateObserver.deregister().catch(() => {
                // Ignore any errors from deregistering that might prevent other waiters
                // from disconnecting.
            });
            e.data.awaitPromise(promise);
        });
    }
    _onError(source, e) {
        this.emit("error" /* CLIENT_ERROR */, {
            source,
            error: e.error,
        });
        if (e.error && !!e.error.unrecoverable) {
            this.emit("unrecoverable_failure" /* CLIENT_UNRECOVERABLE_FAILURE */, {
                source: source,
                error: e.error,
            });
        }
    }
    _onConnected() {
        this.emit("connected" /* CLIENT_CONNECTED */, null);
    }
    _onConnectionError(e) {
        this._onError(HarmonyErrorSource.TRANSPORT, { error: e.data.error });
        this.emit("connection_error" /* CLIENT_CONNECTION_ERROR */, e.data);
    }
    _onAuthenticationError(e) {
        this._onError(HarmonyErrorSource.TRANSPORT, { error: e.data.error });
        this.emit("authentication_error" /* CLIENT_AUTHENTICATION_ERROR */, e.data);
    }
    _onShortSessionDisconnect(e) {
        if (!this._logger) {
            return;
        }
        this._logger
            .logClientEvent({
            source: 'transport',
            source_version: sdkVersion.tagged,
            source_vendor: 'spotify',
            event: e.type,
            event_version: '1.0.0',
            json_data: {
                disconnectCount: e.data.disconnectCount,
                sessionLength: e.data.sessionLength,
            },
        }, 'object')
            .catch((err) => debugLogger.warn(`${e.type} Logging Error:`, err));
    }
    getVersionDescriptor() {
        return Object.assign({}, sdkVersion);
    }
    getSDKId() {
        return `harmony:${sdkVersion.tagged}`;
    }
    getUntaggedSDKId() {
        return `harmony:${sdkVersion.version}`;
    }
    getLogger() {
        return this._logger;
    }
    logAppMetrics(meters, timers) {
        if (!this._logger) {
            throw new TypeError('Invalid logger instance');
        }
        return this._logger.logMetrics(meters, timers);
    }
    request(uri, options) {
        return this._transport.request(uri, options);
    }
    getPublicTransport() {
        return this._transport.toPublic();
    }
    getClientDescriptor() {
        return this._deviceDescriptor.then((descriptor) => {
            return descriptor.toJSON();
        });
    }
    getDeviceDescriptor() {
        return this._deviceDescriptor;
    }
    getUserInfo() {
        return this._transport
            .request('@webapi/v1/me', { responseType: 'json' })
            .then((response) => {
            // TODO: Move this parser to a private function.
            const body = response.body;
            if (response.status !== 200) {
                const error = new HarmonyError(HarmonyErrors.USER_INFO_REQUEST_FAILED_WITH_STATUS, `User info request failed with status ${response.status}`);
                error.status = response.status;
                return Promise.reject(error);
            }
            else if (!body) {
                return Promise.reject(new HarmonyError(HarmonyErrors.USER_INFO_REQUEST_EMPTY_RESPONSE, 'Unexpected empty response.'));
            }
            return {
                display_name: body.display_name,
                followers: body.followers,
                id: body.id,
                images: body.images,
                uri: body.uri,
            };
        });
    }
    getProductState() {
        return this._productStateObserver.getCurrentState();
    }
    setName(name) {
        return this._deviceDescriptor.then(function (descriptor) {
            return descriptor.setName(name);
        });
    }
    setNameTemplate(name) {
        return this.setName(interpolateUA(name));
    }
    setCapability(capability, value) {
        return this._deviceDescriptor.then((descriptor) => {
            return descriptor.setCapability(capability, value);
        });
    }
    notifyError(source, error) {
        this._onError(source, { error });
    }
}
//# sourceMappingURL=client.js.map